import { Injectable } from '@angular/core';
import { IReportColumnDef } from '../../shared-models/reports/report-column.interface';
import { IReportDefinition, ReportCellValue } from '../../shared-models/reports/report-definition.interface';
import { ReportPageOrientation } from '../../shared-models/reports/report-page-orientation.enum';
import { ReportValueType } from '../../shared-models/reports/report-value-type.enum';
import { formatReportCellValue, formatReportDateTime, sumReportColumnValues } from '../../shared-utils/reports/report-format.utils';
import {
  IPdfMakeCreatedPdf,
  IPdfMakeDocumentDefinition,
  IPdfMakeFontsModule,
  IPdfMakeStatic,
  IPdfMakeTableCell,
  PdfMakeContent,
} from './pdfmake.types';

/**
 * Browser-side PDF report renderer built on pdfmake.
 * Features supply an {@link IReportDefinition}; this service owns layout only.
 */
@Injectable({
  providedIn: 'root',
})
export class SharedPdfReportService {
  private pdfMakeInstance: IPdfMakeStatic | null = null;

  /**
   * Build an A4 PDF from a report definition and open it in a new browser tab.
   */
  async openReport(definition: IReportDefinition): Promise<void> {
    const pdf = await this.createPdf(definition);
    const blob = await this.toBlob(pdf);
    const url = URL.createObjectURL(blob);
    const opened = window.open(url, '_blank');
    if (!opened) {
      this.downloadBlob(blob, definition.filename);
    }
    window.setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 60_000);
  }

  /**
   * Build an A4 PDF and trigger a file download.
   */
  async downloadReport(definition: IReportDefinition): Promise<void> {
    const pdf = await this.createPdf(definition);
    const blob = await this.toBlob(pdf);
    this.downloadBlob(blob, definition.filename);
  }

  /**
   * Construct the pdfmake document definition without generating a file.
   * Exposed for unit tests and future adapters.
   */
  buildDocumentDefinition(definition: IReportDefinition): IPdfMakeDocumentDefinition {
    if (!definition.columns.length) {
      throw new Error('At least one report column must be selected');
    }

    const generatedAt = definition.generatedAt ?? new Date();
    const content: PdfMakeContent[] = [
      {
        text: definition.title,
        style: 'title',
      },
    ];

    if (definition.subtitle) {
      content.push({
        text: definition.subtitle,
        style: 'subtitle',
      });
    }

    content.push({
      text: `Generated: ${formatReportDateTime(generatedAt.getTime())}`,
      style: 'generatedAt',
    });

    if (definition.metadata.length > 0) {
      content.push({
        columns: definition.metadata.map((item) => ({
          stack: [
            { text: item.label, style: 'metaLabel' },
            { text: item.value || '—', style: 'metaValue' },
          ],
        })),
        columnGap: 12,
        margin: [0, 8, 0, 4],
      });
    }

    if (definition.summary.length > 0) {
      content.push({
        columns: definition.summary.map((item) => ({
          stack: [
            { text: item.label, style: 'summaryLabel' },
            { text: item.value, style: 'summaryValue' },
          ],
        })),
        columnGap: 12,
        margin: [0, 4, 0, 10],
      });
    }

    content.push(this.buildTable(definition));

    if (definition.footerNote) {
      content.push({
        text: definition.footerNote,
        style: 'footerNote',
        margin: [0, 10, 0, 0],
      });
    }

    return {
      pageSize: 'A4',
      pageOrientation:
        definition.orientation === ReportPageOrientation.portrait ? 'portrait' : 'landscape',
      pageMargins: [28, 36, 28, 40],
      content,
      styles: {
        title: {
          fontSize: 16,
          bold: true,
          color: '#1f2937',
          margin: [0, 0, 0, 2],
        },
        subtitle: {
          fontSize: 10,
          color: '#4b5563',
          margin: [0, 0, 0, 2],
        },
        generatedAt: {
          fontSize: 8,
          color: '#6b7280',
          margin: [0, 0, 0, 6],
        },
        metaLabel: {
          fontSize: 8,
          color: '#6b7280',
        },
        metaValue: {
          fontSize: 9,
          bold: true,
          color: '#111827',
        },
        summaryLabel: {
          fontSize: 8,
          color: '#6b7280',
        },
        summaryValue: {
          fontSize: 11,
          bold: true,
          color: '#111827',
        },
        tableHeader: {
          fontSize: 8,
          bold: true,
          color: '#ffffff',
        },
        tableCell: {
          fontSize: 8,
          color: '#111827',
        },
        totalsCell: {
          fontSize: 8,
          bold: true,
          color: '#111827',
        },
        footerNote: {
          fontSize: 8,
          italics: true,
          color: '#4b5563',
        },
        pageFooter: {
          fontSize: 8,
          color: '#6b7280',
        },
      },
      defaultStyle: {
        fontSize: 9,
        color: '#111827',
      },
      footer: (currentPage: number, pageCount: number) => ({
        columns: [
          {
            text: definition.title,
            style: 'pageFooter',
            alignment: 'left',
          },
          {
            text: `Page ${currentPage} of ${pageCount}`,
            style: 'pageFooter',
            alignment: 'right',
          },
        ],
        margin: [28, 0, 28, 0],
      }),
      info: {
        title: definition.title,
        author: 'Gallix',
      },
    };
  }

  private buildTable(definition: IReportDefinition): PdfMakeContent {
    const headerRow: IPdfMakeTableCell[] = definition.columns.map((column: IReportColumnDef) => ({
      text: column.label,
      style: 'tableHeader',
      alignment: column.align ?? this.defaultAlign(column),
      fillColor: '#1f4e79',
      bold: true,
    }));

    const body: IPdfMakeTableCell[][] = [headerRow];

    definition.rows.forEach((row: Record<string, ReportCellValue>, rowIndex: number) => {
      const fillColor = rowIndex % 2 === 0 ? '#ffffff' : '#f3f4f6';
      body.push(
        definition.columns.map((column: IReportColumnDef) => ({
          text: formatReportCellValue(row[column.id], column.valueType ?? ReportValueType.string),
          style: 'tableCell',
          alignment: column.align ?? this.defaultAlign(column),
          fillColor,
        })),
      );
    });

    const totalsRow = this.buildTotalsRow(definition);
    if (totalsRow) {
      body.push(totalsRow);
    }

    const totalWeight = definition.columns.reduce(
      (sum: number, column: IReportColumnDef) => sum + (column.widthWeight ?? 1),
      0,
    );
    const widths = definition.columns.map((column: IReportColumnDef) => {
      const weight = column.widthWeight ?? 1;
      const percent = totalWeight > 0 ? (weight / totalWeight) * 100 : 100 / definition.columns.length;
      return `${percent}%`;
    });

    return {
      table: {
        headerRows: 1,
        widths,
        body,
      },
      layout: {
        hLineWidth: (): number => 0.4,
        vLineWidth: (): number => 0.2,
        hLineColor: (): string => '#d1d5db',
        vLineColor: (): string => '#e5e7eb',
        paddingLeft: (): number => 4,
        paddingRight: (): number => 4,
        paddingTop: (): number => 3,
        paddingBottom: (): number => 3,
      },
      margin: [0, 4, 0, 0],
    };
  }

  private buildTotalsRow(definition: IReportDefinition): IPdfMakeTableCell[] | null {
    const hasTotals = definition.columns.some((column: IReportColumnDef) => column.includeInTotals);
    if (!hasTotals || definition.rows.length === 0) {
      return null;
    }

    return definition.columns.map((column: IReportColumnDef, index: number) => {
      if (index === 0 && !column.includeInTotals) {
        return {
          text: 'Totals',
          style: 'totalsCell',
          alignment: 'left',
          fillColor: '#e5e7eb',
          bold: true,
        };
      }
      if (!column.includeInTotals) {
        return {
          text: '',
          style: 'totalsCell',
          fillColor: '#e5e7eb',
        };
      }
      const total = sumReportColumnValues(
        definition.rows.map((row: Record<string, ReportCellValue>) => row[column.id]),
      );
      return {
        text: formatReportCellValue(total, column.valueType ?? ReportValueType.number),
        style: 'totalsCell',
        alignment: column.align ?? this.defaultAlign(column),
        fillColor: '#e5e7eb',
        bold: true,
      };
    });
  }

  private defaultAlign(column: IReportColumnDef): 'left' | 'right' {
    const type = column.valueType ?? ReportValueType.string;
    return type === ReportValueType.string || type === ReportValueType.date || type === ReportValueType.dateTime
      ? 'left'
      : 'right';
  }

  private async createPdf(definition: IReportDefinition): Promise<IPdfMakeCreatedPdf> {
    const pdfMake = await this.loadPdfMake();
    return pdfMake.createPdf(this.buildDocumentDefinition(definition));
  }

  private async loadPdfMake(): Promise<IPdfMakeStatic> {
    if (this.pdfMakeInstance) {
      return this.pdfMakeInstance;
    }

    const pdfMakeModule = await import('pdfmake/build/pdfmake');
    const fontsModule = await import('pdfmake/build/vfs_fonts');
    const pdfMake = (pdfMakeModule as { default?: IPdfMakeStatic }).default
      ?? (pdfMakeModule as unknown as IPdfMakeStatic);
    const fonts = (fontsModule as { default?: IPdfMakeFontsModule }).default
      ?? (fontsModule as unknown as IPdfMakeFontsModule);

    pdfMake.vfs = fonts.pdfMake?.vfs ?? (fonts as unknown as { vfs: Record<string, string> }).vfs;
    this.pdfMakeInstance = pdfMake;
    return pdfMake;
  }

  private toBlob(pdf: IPdfMakeCreatedPdf): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        pdf.getBlob((blob: Blob) => {
          resolve(blob);
        });
      } catch (error) {
        reject(error instanceof Error ? error : new Error('Failed to create PDF blob'));
      }
    });
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
